import { Download01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SHARE_MOBILE_CARDCMP } from 'src/app/Track/RadarOrderDetail/sections/common/shareMobile.styles';
import ButtonCmp from 'src/components/ButtonCmp';
import CardCmp from 'src/components/CardCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { SharedLinkDocumentGroup } from 'src/services/CustomerService';
import { downloadSharedDocument } from 'src/services/ShareLinkService';
import { getFileExtensionTypeImage, onError } from 'src/utils/CommonFunctions';
import { saveBlob } from 'src/utils/saveBlob';
import WalToast from 'src/utils/WalToast';

/**
 * Documents the customer chose to share on a public tracking link (cp-meeting-changes F3). The list
 * comes from the share payload (id, type, name, date, group; never a storage URL) and each download
 * goes through the token-checked share endpoint, so it stops working when the link does.
 */
export interface ISharedDocument {
    id: number;
    documentType: string;
    fileName: string | null;
    createdAt: string | null;
    group: SharedLinkDocumentGroup;
}

interface IProps {
    documents?: ISharedDocument[] | null;
    kind: 'order' | 'radar';
    // Share-link mobile sheet (Order tab): the same Figma card styling as the cards beside it.
    isShareMobile?: boolean;
}

const SharedDocumentRow = ({ doc, kind, isShareMobile }: { doc: ISharedDocument; kind: IProps['kind']; isShareMobile?: boolean }) => {
    const { t } = useTranslation();
    const nameRef = useRef<HTMLParagraphElement>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const name = doc.fileName || `${doc.documentType}-${doc.id}`;
    const extension = name.split('.').pop()?.toLowerCase() || '';

    const handleDownload = async () => {
        setIsDownloading(true);

        try {
            saveBlob(await downloadSharedDocument(kind, doc.id), name);
        } catch {
            WalToast.error(t('ShareRadarOrder.Download_Failed'));
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <li className={`flex gap-x-3 items-center py-1.5 ${isShareMobile ? '' : '-mx-1.5 px-1.5 rounded-md transition-colors duration-150 hover:bg-utilityGray100'}`}>
            <figure className="flex-none">
                <img src={getFileExtensionTypeImage(extension)} alt="" onError={onError} className="w-[30px] h-[30px] flex-none object-contain" />
            </figure>
            <div className="flex-1 overflow-hidden">
                <TruncateTextCmp message={name} refName={nameRef}>
                    <p className="text-xs leading-[1.5] font-medium text-textSecondary truncate" ref={nameRef}>
                        {name}
                    </p>
                </TruncateTextCmp>
                <p className={isShareMobile ? 'text-xs leading-[1.5] font-normal text-grayLight600' : 'text-[10px] leading-[14px] font-normal text-gray500'}>
                    {t(`SharedLinkSettings.Group_${doc.group}`)}
                </p>
            </div>
            {/* The spinner goes IN the icon slot rather than through ButtonCmp's `loading` prop: that prop renders a
                spinner *beside* the icon, so a download in flight showed both at once and the row widened by the extra
                `pr-2`. Swapping the glyph keeps one indicator, the same w-4 h-4 box and the same `gap-1`, so nothing
                shifts while the file streams. `disabled` still guards against a second click. */}
            <ButtonCmp
                type="button"
                size="none"
                className="btn-link-secondary flex-none"
                icon={isDownloading ? <span className="loading loading-spinner w-4 h-4 text-xs" /> : <Download01 className="w-4 h-4" />}
                disabled={isDownloading}
                onClick={handleDownload}
            >
                {t('ShareRadarOrder.Download')}
            </ButtonCmp>
        </li>
    );
};

// Desktop rows match the Order Documents widget (space-y-1, hover row, 10px meta line); the share-link
// mobile sheet keeps its Figma divided list.
export const SharedDocumentsList = ({ documents, kind, isShareMobile = false }: IProps) => (
    <ul className={isShareMobile ? 'divide-y divide-utilityGray200' : 'space-y-1'}>
        {(documents ?? []).map((doc) => (
            <SharedDocumentRow key={doc.id} doc={doc} kind={kind} isShareMobile={isShareMobile} />
        ))}
    </ul>
);

// Same list area as the Order Documents widget: 12px padding, capped at 160px and scrolls.
export const SHARED_DOCUMENTS_CONTENT_CLASS = 'widget-card-content-wrap px-3 py-3 max-h-[160px] overflow-auto custom-scrollbar-v2';

/** Card for the shared pages; renders nothing when the customer shares no documents (the default). */
const SharedDocumentsSection = ({ documents, kind, isShareMobile = false }: IProps) => {
    const { t } = useTranslation();

    if (!documents?.length) return null;

    return (
        <CardCmp
            headerTitle={t('ShareRadarOrder.Documents_Title')}
            {...(isShareMobile
                ? SHARE_MOBILE_CARDCMP
                : {
                      headerParentClassName: '!rounded-t-lg !px-0 !mx-3',
                      isCardContentWithoutSpacing: true,
                      childrenClassName: SHARED_DOCUMENTS_CONTENT_CLASS,
                  })}
        >
            <SharedDocumentsList documents={documents} kind={kind} isShareMobile={isShareMobile} />
        </CardCmp>
    );
};

export default SharedDocumentsSection;
